import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fastapi } from '../services/api';
import { GhostInput } from '../components/ui/GhostInput';
import BtnTaratic from '../components/ui/BtnTaratic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey,
  faLock,
  faCheckCircle,
  faExclamationTriangle,
  faArrowLeft,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError(t('resetPassword.invalidToken', 'Token no válido o no proporcionado.'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordsMismatch', 'Las contraseñas no coinciden.'));
      return;
    }
    setError(null);
    setLoading(true);

    try {
      await fastapi.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          t('resetPassword.serverError', 'Error al conectar con el servidor.')
      );
    } finally {
      setLoading(false);
    }
  };

  // State A: Invalid or missing token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center dark-bg p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="relative bg-black/50 backdrop-filter backdrop-blur-xl p-8 sm:p-10 rounded-3xl shadow-2xl w-full max-w-md text-center shadow-inner-glow shadow-inner-glow-black-md">
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5 text-red-400 text-2xl shadow-inner-glow">
            <FontAwesomeIcon icon={faExclamationTriangle} className="icon-inner-glow-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 tracking-tight">
            {t('resetPassword.invalidTitle', 'Enlace no válido')}
          </h1>
          <p className="text-white/70 mb-8 text-sm sm:text-base leading-relaxed">
            {t('resetPassword.invalidMessage', 'El enlace para restablecer la contraseña no es válido o ha expirado.')}
          </p>
          <BtnTaratic
            color="ghost-dark"
            size="md"
            className="w-full flex justify-center items-center gap-2 py-3 text-base font-semibold"
            onClick={() => navigate('/login')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
            <span>{t('resetPassword.backToLogin', 'Volver a inicio de sesión')}</span>
          </BtnTaratic>
        </div>
      </div>
    );
  }

  // State B: Password Reset Form / Success
  return (
    <div className="min-h-screen flex items-center justify-center dark-bg p-4 sm:p-6 lg:p-8 overflow-x-hidden">
      <div className="relative bg-black/50 backdrop-filter backdrop-blur-xl p-8 sm:p-10 rounded-3xl shadow-2xl w-full max-w-md shadow-inner-glow shadow-inner-glow-black-md">
        <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mx-auto mb-5 text-indigo-400 text-2xl shadow-inner-glow">
          <FontAwesomeIcon icon={faKey} className="icon-inner-glow-white" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-2 text-center tracking-tight">
          {t('resetPassword.title', 'Restablecer Contraseña')}
        </h1>
        <p className="text-white/60 text-sm text-center mb-8">
          {t('resetPassword.subtitle', 'Introduce tu nueva contraseña a continuación.')}
        </p>

        {success ? (
          <div className="text-center">
            <div className="bg-green-500/10 border border-green-500/20 p-5 rounded-2xl mb-8 shadow-inner-glow">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 text-green-400 text-xl">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <p className="text-green-300 font-medium text-sm sm:text-base">
                {t('resetPassword.successMessage', 'Tu contraseña ha sido actualizada correctamente.')}
              </p>
            </div>
            <BtnTaratic
              color="green"
              size="md"
              className="w-full flex justify-center items-center gap-2 py-3 text-base font-semibold"
              onClick={() => navigate('/login')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-sm" />
              <span>{t('resetPassword.goToLogin', 'Ir a iniciar sesión')}</span>
            </BtnTaratic>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-2xl text-center shadow-inner-glow">
                <p className="text-red-300 text-sm font-medium">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="pass" className="block text-sm font-medium text-white/90 mb-1.5">
                {t('resetPassword.newPassword', 'Nueva Contraseña')}
              </label>
              <div className="relative">
                <GhostInput
                  type="password"
                  id="pass"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="pl-11"
                />
                <FontAwesomeIcon
                  icon={faLock}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm pointer-events-none"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPass" className="block text-sm font-medium text-white/90 mb-1.5">
                {t('resetPassword.confirmPassword', 'Confirmar Contraseña')}
              </label>
              <div className="relative">
                <GhostInput
                  type="password"
                  id="confirmPass"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="pl-11"
                />
                <FontAwesomeIcon
                  icon={faLock}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm pointer-events-none"
                />
              </div>
            </div>

            <BtnTaratic
              type="submit"
              color="green"
              size="md"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 py-3 text-base font-semibold"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-sm" />
                  <span>{t('resetPassword.saving', 'Guardando...')}</span>
                </>
              ) : (
                <span>{t('resetPassword.saveButton', 'Guardar nueva contraseña')}</span>
              )}
            </BtnTaratic>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="btn-taratic btn-ghost-dark text-xs transition-colors inline-flex items-center gap-1.5 underline"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />
                <span>{t('resetPassword.cancel', 'Cancelar y volver a login')}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
